import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { partnersApi } from '../../api/partners';
import { ApiError } from '../../api/client';
import { RecordHeader } from '../../components/RecordHeader';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { useToast } from '../../components/Toast';
import { PartnerDdCaseScoringTab } from './PartnerDdCaseScoringTab';
import { PartnerContactsTab } from './PartnerContactsTab';
import { PartnerOfficesTab } from './PartnerOfficesTab';
import { PartnerBankInfoTab } from './PartnerBankInfoTab';
import { PartnerDocumentsTab } from './PartnerDocumentsTab';
import { PartnerGovernanceTab } from './PartnerGovernanceTab';
import { PartnerReferencesTab } from './PartnerReferencesTab';
import { PartnerCorrectiveActionsTab } from './PartnerCorrectiveActionsTab';
import { PartnerFinancialCapacityTab } from './PartnerFinancialCapacityTab';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { CommentsThread } from '../../components/CommentsThread';
import { PARTNER_DD_CASE_STAGES } from '../../config/workflow-stages';
import { PartnerSafeguardingTab } from './PartnerSafeguardingTab';
import { PartnerPoliciesTab } from './PartnerPoliciesTab';
import { PartnerOrganizationProfileTab } from './PartnerOrganizationProfileTab';
import { PartnerProgrammeCapacityTab } from './PartnerProgrammeCapacityTab';
import { PartnerSubPartnersTab } from './PartnerSubPartnersTab';
import { PartnerProjectsTab } from './PartnerProjectsTab';
import { usePermission } from '../../auth/AuthContext';

const DD_STATUS_LABELS: Record<string, string> = {
  prospective: 'Prospective', dd_draft: 'DD Draft', submitted: 'Submitted', under_review: 'Under Review',
  additional_info_required: 'Additional Info Required', approved: 'Approved', approved_with_conditions: 'Approved with Conditions',
  rejected: 'Rejected', renewal_due: 'Renewal Due', expired: 'Expired',
};

/** The DD Case's own status - the shared WorkflowState enum, genuinely distinct from the partner's due_diligence_status above. */
const CASE_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', under_review: 'Under Review', returned: 'Returned for Redraft',
  clarification_required: 'Additional Information Required', approved: 'Approved', rejected: 'Rejected',
  cancelled: 'Cancelled', in_progress: 'In Progress', completed: 'Completed', closed: 'Closed', archived: 'Archived',
};

/** Due Diligence Case management within a Partner's workspace - list existing cases, start a new one, and drive the lifecycle + scoring for the active one. */
function DueDiligenceCasesSection({ partnerId }: { partnerId: number }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [returnComment, setReturnComment] = useState('');
  const [decisionNotes, setDecisionNotes] = useState('');
  const [conditionFinding, setConditionFinding] = useState('');
  const [conditionAction, setConditionAction] = useState('');
  const [conditionDueDate, setConditionDueDate] = useState('');

  const casesQuery = useQuery({ queryKey: ['partner-dd-cases', partnerId], queryFn: () => partnersApi.listCases(partnerId) });
  const caseDetailQuery = useQuery({ queryKey: ['dd-case', selectedCaseId], queryFn: () => partnersApi.getCase(selectedCaseId as number), enabled: !!selectedCaseId });

  const { register, handleSubmit, reset } = useForm<{ assessmentType: string }>();
  const createCaseMutation = useMutation({
    mutationFn: (values: { assessmentType: string }) => partnersApi.createCase(partnerId, values.assessmentType),
    onSuccess: (result) => { showSuccess(`Case created - ${result.data.caseReference}`); queryClient.invalidateQueries({ queryKey: ['partner-dd-cases', partnerId] }); setShowCreateForm(false); reset(); setSelectedCaseId(result.data.id); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create case.'),
  });

  const invalidateCase = () => { queryClient.invalidateQueries({ queryKey: ['dd-case', selectedCaseId] }); queryClient.invalidateQueries({ queryKey: ['partner-dd-cases', partnerId] }); };
  const submitMutation = useMutation({ mutationFn: () => partnersApi.submitCase(selectedCaseId as number), onSuccess: () => { showSuccess('Submitted.'); invalidateCase(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const startReviewMutation = useMutation({ mutationFn: () => partnersApi.startReview(selectedCaseId as number), onSuccess: () => { showSuccess('Review started.'); invalidateCase(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const returnMutation = useMutation({
    mutationFn: () => partnersApi.returnForRedraft(selectedCaseId as number, returnComment),
    onSuccess: () => { showSuccess('Returned for redraft.'); invalidateCase(); setReturnComment(''); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });
  const resubmitMutation = useMutation({ mutationFn: () => partnersApi.resubmit(selectedCaseId as number), onSuccess: () => { showSuccess('Resubmitted.'); invalidateCase(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const decideMutation = useMutation({
    mutationFn: (decisionType: string) => partnersApi.decide(selectedCaseId as number, {
      decisionType, notes: decisionNotes,
      conditions: decisionType === 'approved_with_conditions' ? [{ finding: conditionFinding, actionRequired: conditionAction, dueDate: conditionDueDate || undefined }] : undefined,
    }),
    onSuccess: () => { showSuccess('Decision recorded.'); invalidateCase(); setDecisionNotes(''); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  const ddCase = caseDetailQuery.data?.data;

  return (
    <div>
      <div className="panel">
        <div className="panel-body">
          <button onClick={() => setShowCreateForm((v) => !v)}>{showCreateForm ? 'Cancel' : 'Start New Due Diligence Case'}</button>
          {showCreateForm && (
            <form className="inline-form" onSubmit={handleSubmit((values) => createCaseMutation.mutate(values))} style={{ marginTop: 8 }}>
              <label>
                Assessment Type
                <select {...register('assessmentType', { required: true })}>
                  <option value="initial">Initial</option><option value="renewal">Renewal</option><option value="special_review">Special Review</option>
                </select>
              </label>
              <button type="submit" disabled={createCaseMutation.isPending}>{createCaseMutation.isPending ? 'Creating…' : 'Create'}</button>
            </form>
          )}
          {casesQuery.data && (
            <DataTable
              rows={casesQuery.data.data}
              rowKey={(c) => c.id}
              emptyMessage="No due diligence cases yet."
              columns={[
                { header: 'Case Reference', render: (c) => <a onClick={() => setSelectedCaseId(c.id)} className="table-link">{c.case_reference}</a> },
                { header: 'Type', render: (c) => c.assessment_type },
                { header: 'Status', render: (c) => <StatusBadge status={c.status} /> },
                { header: 'Decision', render: (c) => c.decision_type ?? '-' },
                { header: 'Created', render: (c) => c.created_at },
              ]}
            />
          )}
        </div>
      </div>

      {ddCase && (
        <div className="panel">
          <div className="panel-body">
            <h2>Case {ddCase.case_reference}</h2>
            <p><StatusBadge status={ddCase.status} label={CASE_STATUS_LABELS[ddCase.status]} /> {ddCase.decision_type && `- ${ddCase.decision_type}`}</p>

            <div className="row-actions">
              {ddCase.status === 'draft' && <button onClick={() => submitMutation.mutate()} disabled={submitMutation.isPending}>Submit</button>}
              {ddCase.status === 'submitted' && <button onClick={() => startReviewMutation.mutate()} disabled={startReviewMutation.isPending}>Start Review</button>}
              {(ddCase.status === 'returned' || ddCase.status === 'clarification_required') && <button onClick={() => resubmitMutation.mutate()} disabled={resubmitMutation.isPending}>Resubmit</button>}
            </div>

            {ddCase.status === 'under_review' && (
              <>
                <PartnerDdCaseScoringTab ddCase={{ id: ddCase.id, scoring_model_id: ddCase.scoring_model_id }} />

                <div className="panel">
                  <div className="panel-body">
                    <h3>Return for Redraft</h3>
                    <div className="inline-form">
                      <label>Comment (required)<input value={returnComment} onChange={(e) => setReturnComment(e.target.value)} /></label>
                      <button onClick={() => returnMutation.mutate()} disabled={!returnComment || returnMutation.isPending}>Return for Redraft</button>
                    </div>

                    <h3>Decision</h3>
                    <div className="inline-form">
                      <label>Notes (required)<input value={decisionNotes} onChange={(e) => setDecisionNotes(e.target.value)} /></label>
                      <button onClick={() => decideMutation.mutate('approved')} disabled={!decisionNotes || decideMutation.isPending}>Approve</button>
                      <button className="button-danger" onClick={() => decideMutation.mutate('rejected')} disabled={!decisionNotes || decideMutation.isPending}>Reject</button>
                    </div>

                    <h3>Approve with Conditions</h3>
                    <p className="muted">At least one condition is required - it becomes a real corrective action, tracked separately.</p>
                    <div className="inline-form">
                      <label>Finding<input value={conditionFinding} onChange={(e) => setConditionFinding(e.target.value)} /></label>
                      <label>Action Required<input value={conditionAction} onChange={(e) => setConditionAction(e.target.value)} /></label>
                      <label>Due Date<input type="date" value={conditionDueDate} onChange={(e) => setConditionDueDate(e.target.value)} /></label>
                      <button onClick={() => decideMutation.mutate('approved_with_conditions')} disabled={!decisionNotes || !conditionFinding || !conditionAction || decideMutation.isPending}>Approve with Conditions</button>
                    </div>
                  </div>
                </div>
              </>
            )}

            <WorkflowTrackingTab entityType="partner_due_diligence_case" entityId={ddCase.id} actionStageMap={PARTNER_DD_CASE_STAGES} statusLabels={CASE_STATUS_LABELS} />
            <CommentsThread entityType="partner_due_diligence_case" entityId={ddCase.id} section="due_diligence" heading="Comments for this Case" />
          </div>
        </div>
      )}
    </div>
  );
}

export function PartnerWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const partnerId = Number(id);
  const partnerQuery = useQuery({ queryKey: ['partner', partnerId], queryFn: () => partnersApi.get(partnerId) });
  const canViewBankInfo = usePermission('partner.due_diligence.bank_info.view');
  const canViewFinancialCapacity = usePermission('partner.due_diligence.financial_capacity.view');
  const canViewSafeguarding = usePermission('partner.due_diligence.safeguarding.view');

  if (!partnerQuery.data) return null;
  const p = partnerQuery.data.data;

  return (
    <div className="page">
      <Link to="/partners">&larr; Back to Partner Registry</Link>

      <RecordHeader
        title={`${p.legal_name}${p.acronym ? ` (${p.acronym})` : ''}`}
        entityType="partner"
        entityId={partnerId}
        readOnly
        fields={[
          { label: 'Partner ID', value: p.partner_reference },
          { label: 'Organization Type', value: p.organization_type },
          { label: 'Due Diligence Status', value: <StatusBadge status={p.due_diligence_status} label={DD_STATUS_LABELS[p.due_diligence_status]} /> },
          { label: 'Risk Rating', value: p.current_risk_rating ? p.current_risk_rating.charAt(0).toUpperCase() + p.current_risk_rating.slice(1) : '-' },
          { label: 'Partnership Status', value: <StatusBadge status={p.partnership_status} /> },
          { label: 'Last Review', value: p.last_review_date ?? '-' },
          { label: 'Next Review', value: p.next_review_date ?? '-' },
        ]}
      />

      <WorkspaceTabs
        tabs={[
          { key: 'overview', label: 'Organization Profile', content: <PartnerOrganizationProfileTab partnerId={partnerId} /> },
          { key: 'due_diligence', label: 'Due Diligence', content: <DueDiligenceCasesSection partnerId={partnerId} /> },
          { key: 'programme_capacity', label: 'Programme & Technical Capacity', content: <PartnerProgrammeCapacityTab partnerId={partnerId} /> },
          { key: 'sub_partners', label: 'Sub-Partners', content: <PartnerSubPartnersTab partnerId={partnerId} /> },
          { key: 'projects', label: 'Projects & Programmes', content: <PartnerProjectsTab partnerId={partnerId} /> },
          { key: 'contacts', label: 'Contacts & Key Personnel', content: <PartnerContactsTab partnerId={partnerId} /> },
          { key: 'offices', label: 'Office Locations', content: <PartnerOfficesTab partnerId={partnerId} /> },
          { key: 'documents', label: 'Documents', content: <PartnerDocumentsTab partnerId={partnerId} /> },
          { key: 'governance', label: 'Governance', content: <PartnerGovernanceTab partnerId={partnerId} /> },
          { key: 'references', label: 'References & Verification', content: <PartnerReferencesTab partnerId={partnerId} /> },
          { key: 'corrective_actions', label: 'Corrective Actions', content: <PartnerCorrectiveActionsTab partnerId={partnerId} /> },
          { key: 'policies', label: 'Policies & Compliance', content: <PartnerPoliciesTab partnerId={partnerId} /> },
          ...(canViewFinancialCapacity ? [{ key: 'financial_capacity', label: 'Financial Capacity', content: <PartnerFinancialCapacityTab partnerId={partnerId} /> }] : []),
          ...(canViewSafeguarding ? [{ key: 'safeguarding', label: 'Safeguarding / PSEA', content: <PartnerSafeguardingTab partnerId={partnerId} /> }] : []),
          ...(canViewBankInfo ? [{ key: 'bank_info', label: 'Bank Information', content: <PartnerBankInfoTab partnerId={partnerId} /> }] : []),
        ]}
      />
    </div>
  );
}
